import { ensureOk } from "@/api/apiError";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { components } from "@/api/generated-types";
import type { AdminUser } from "@/types";

export type PaginatedAdminUsers =
  components["schemas"]["PaginatedResponse_AdminUser_"];

export interface AdminListQuery {
  q?: string;
  skip?: number;
  limit?: number;
}

function buildQueryString(params: AdminListQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  search.set("skip", String(params.skip ?? 0));
  search.set("limit", String(params.limit ?? 20));
  return `?${search.toString()}`;
}

async function fetchAdminUsers(
  params: AdminListQuery,
): Promise<PaginatedAdminUsers> {
  const response = await httpClient(`/admin/users${buildQueryString(params)}`);
  await ensureOk(response);
  return response.json() as Promise<PaginatedAdminUsers>;
}

async function fetchAdminUser(userId: string): Promise<AdminUser> {
  const response = await httpClient(
    `/admin/users/${encodeURIComponent(userId)}`,
  );
  await ensureOk(response);
  return response.json() as Promise<AdminUser>;
}

async function deleteAdminUser(userId: string): Promise<void> {
  const response = await httpClient(
    `/admin/users/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
  await ensureOk(response);
}

async function addGroupToUser(input: {
  userId: string;
  groupName: string;
}): Promise<AdminUser> {
  const response = await httpClient(
    `/admin/users/${encodeURIComponent(input.userId)}/groups/${encodeURIComponent(input.groupName)}`,
    { method: "POST" },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminUser>;
}

async function removeGroupFromUser(input: {
  userId: string;
  groupName: string;
}): Promise<AdminUser> {
  const response = await httpClient(
    `/admin/users/${encodeURIComponent(input.userId)}/groups/${encodeURIComponent(input.groupName)}`,
    { method: "DELETE" },
  );
  await ensureOk(response);
  return response.json() as Promise<AdminUser>;
}

/** US-10: admin listing of all users (search + pagination server-side, 403 for non-admins). */
export function useAdminUsers(params: AdminListQuery = {}) {
  return useQuery({
    queryKey: ["admin", "users", params],
    queryFn: () => fetchAdminUsers(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useAdminUser(userId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "users", "detail", userId],
    queryFn: () => fetchAdminUser(userId as string),
    enabled: Boolean(userId),
    staleTime: 30 * 1000,
  });
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}

export function useAddGroupToUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addGroupToUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}

export function useRemoveGroupFromUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeGroupFromUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "groups"] });
    },
  });
}
